import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { CarIcon, UserXIcon, DatabaseIcon, LinkIcon, AlertTriangleIcon, ShieldQuestionIcon, ReceiptIcon, ScanFaceIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const POINTS = [{
  icon: CarIcon,
  text: 'تعرض موقع المركبة فقط'
}, {
  icon: UserXIcon,
  text: 'لا تربط المركبة بالسائق بشكل مباشر'
}, {
  icon: DatabaseIcon,
  text: 'لا توفر بيانات السائق في الوقت الفعلي'
}, {
  icon: LinkIcon,
  text: 'لا يوجد سجل موحد للسائق'
}];
export function GapSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-394">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-395">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.03) 0%, transparent 55%)'
      }} data-id="element-396" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v48H0V0zm1 1h46v46H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-397" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-398">
        <SharjahPoliceEmblem variant="light" data-id="element-399" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-400" />
      </motion.div>

      {/* ===== TITLE ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.2
    }} className="relative z-10 px-[clamp(2rem,6vw,6rem)] mt-[clamp(0.75rem,2vh,1.5rem)]" data-id="element-401">
        <h2 className="font-arabic-display font-black text-center leading-[1.15]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.4rem, 2.3vw, 2.1rem)'
      }} data-id="element-402">
          محدوديه انظمة التتبع المتاحه
        </h2>
        <div className="mx-auto mt-2.5 h-[2.5px] rounded-full" style={{
        width: 'clamp(3rem, 5vw, 4rem)',
        background: '#2FA99C',
        opacity: 0.45
      }} data-id="element-403" />
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-10 flex-1 min-h-0 grid px-[clamp(2rem,5vw,5rem)] pt-[clamp(1rem,2.5vh,2rem)]" style={{
      gridTemplateColumns: '1.1fr 1fr',
      gap: 'clamp(1.5rem, 3vw, 3rem)'
    }} data-id="element-404">
        {/* RIGHT COLUMN (first in RTL) — Bullet List */}
        <motion.div initial={{
        opacity: 0,
        x: -14
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 0.35
      }} className="flex flex-col justify-center" data-id="element-405">
          <h3 className="font-arabic-display font-bold text-right mb-[clamp(1rem,2.5vh,1.75rem)]" style={{
          color: '#6E7A8F',
          fontSize: 'clamp(0.95rem, 1.3vw, 1.2rem)',
          letterSpacing: '0.02em'
        }} data-id="element-406">
            الأنظمة التقليدية
          </h3>

          <div className="flex flex-col gap-[clamp(0.85rem,2vh,1.5rem)]" data-id="element-407">
            {POINTS.map((point, i) => {
            const Icon = point.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={base ?? {
              duration: 0.5,
              delay: 0.45 + i * 0.1
            }} className="flex items-center gap-[clamp(0.5rem,0.8vw,0.7rem)]" dir="rtl" data-id="element-408">
                  <div className="shrink-0 rounded-full flex items-center justify-center" style={{
                width: 'clamp(1.85rem, 2.5vw, 2.25rem)',
                height: 'clamp(1.85rem, 2.5vw, 2.25rem)',
                background: 'rgba(110,122,143,0.07)',
                border: '1px solid rgba(110,122,143,0.12)'
              }} data-id="element-409">
                    <Icon style={{
                  width: 'clamp(0.85rem, 1vw, 0.95rem)',
                  height: 'clamp(0.85rem, 1vw, 0.95rem)',
                  color: '#6E7A8F'
                }} strokeWidth={1.8} data-id="element-410" />
                  </div>
                  <span className="font-arabic-body font-semibold" style={{
                color: '#1A1F2B',
                fontSize: 'clamp(0.85rem, 1.05vw, 1rem)'
              }} data-id="element-411">
                    {point.text}
                  </span>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* LEFT COLUMN (second in RTL) — Two Premium Cards */}
        <div className="flex flex-col justify-center gap-[clamp(1rem,2.5vh,1.75rem)]" data-id="element-412">
          {/* أمنياً Card */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.7,
          delay: 0.7
        }} className="rounded-xl p-[clamp(1.25rem,2.5vh,1.75rem)]" style={{
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(110,122,143,0.1)',
          boxShadow: '0 6px 24px -6px rgba(14,93,85,0.06)'
        }} data-id="element-413">
            <div className="flex items-center gap-[clamp(0.5rem,0.8vw,0.7rem)] mb-[clamp(0.6rem,1.5vh,1rem)]" dir="rtl" data-id="element-414">
              <div className="shrink-0 rounded-full flex items-center justify-center" style={{
              width: 'clamp(2rem, 2.8vw, 2.5rem)',
              height: 'clamp(2rem, 2.8vw, 2.5rem)',
              background: 'rgba(110,122,143,0.08)',
              border: '1px solid rgba(110,122,143,0.12)'
            }} data-id="element-415">
                <ShieldQuestionIcon style={{
                width: 'clamp(0.9rem, 1.1vw, 1.05rem)',
                height: 'clamp(0.9rem, 1.1vw, 1.05rem)',
                color: '#6E7A8F'
              }} strokeWidth={1.8} data-id="element-416" />
              </div>
              <h4 className="font-arabic-display font-bold" style={{
              color: '#4A5568',
              fontSize: 'clamp(1rem, 1.3vw, 1.2rem)'
            }} data-id="element-417">
                أمنياً
              </h4>
            </div>
            <div className="flex items-start gap-[clamp(0.5rem,0.8vw,0.7rem)]" dir="rtl" data-id="element-418">
              <div className="shrink-0 mt-1" data-id="element-419">
                <ScanFaceIcon style={{
                width: 'clamp(0.75rem, 0.9vw, 0.85rem)',
                height: 'clamp(0.75rem, 0.9vw, 0.85rem)',
                color: '#6E7A8F',
                opacity: 0.5
              }} strokeWidth={2} data-id="element-420" />
              </div>
              <p className="font-arabic-body leading-[1.8]" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.82rem, 1vw, 0.95rem)',
              opacity: 0.85
            }} data-id="element-421">
                لا يمكن تحديد هوية السائق في الوقت الفعلي
              </p>
            </div>
            {/* Visual: unknown identity */}
            <div className="flex items-center justify-center gap-3 mt-[clamp(0.75rem,1.5vh,1rem)] opacity-40" data-id="element-422">
              <CarIcon size={16} color="#6E7A8F" strokeWidth={1.5} data-id="element-423" />
              <div className="flex items-center gap-1" data-id="element-424">
                <div className="w-4 h-px bg-[#6E7A8F]" data-id="element-425" />
                <span className="text-[#6E7A8F] text-xs font-bold" data-id="element-426">?</span>
                <div className="w-4 h-px bg-[#6E7A8F]" data-id="element-427" />
              </div>
              <div className="relative" data-id="element-428">
                <UserXIcon size={16} color="#6E7A8F" strokeWidth={1.5} data-id="element-429" />
              </div>
            </div>
          </motion.div>

          {/* مرورياً Card */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.7,
          delay: 0.9
        }} className="rounded-xl p-[clamp(1.25rem,2.5vh,1.75rem)]" style={{
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(212,85,58,0.1)',
          boxShadow: '0 6px 24px -6px rgba(212,85,58,0.06)'
        }} data-id="element-430">
            <div className="flex items-center gap-[clamp(0.5rem,0.8vw,0.7rem)] mb-[clamp(0.6rem,1.5vh,1rem)]" dir="rtl" data-id="element-431">
              <div className="shrink-0 rounded-full flex items-center justify-center" style={{
              width: 'clamp(2rem, 2.8vw, 2.5rem)',
              height: 'clamp(2rem, 2.8vw, 2.5rem)',
              background: 'rgba(212,85,58,0.08)',
              border: '1px solid rgba(212,85,58,0.15)'
            }} data-id="element-432">
                <ReceiptIcon style={{
                width: 'clamp(0.9rem, 1.1vw, 1.05rem)',
                height: 'clamp(0.9rem, 1.1vw, 1.05rem)',
                color: '#D4553A'
              }} strokeWidth={1.8} data-id="element-433" />
              </div>
              <h4 className="font-arabic-display font-bold" style={{
              color: '#D4553A',
              fontSize: 'clamp(1rem, 1.3vw, 1.2rem)'
            }} data-id="element-434">
                مرورياً
              </h4>
            </div>
            <div className="flex items-start gap-[clamp(0.5rem,0.8vw,0.7rem)]" dir="rtl" data-id="element-435">
              <div className="shrink-0 mt-1" data-id="element-436">
                <AlertTriangleIcon style={{
                width: 'clamp(0.75rem, 0.9vw, 0.85rem)',
                height: 'clamp(0.75rem, 0.9vw, 0.85rem)',
                color: '#D4553A',
                opacity: 0.5
              }} strokeWidth={2} data-id="element-437" />
              </div>
              <p className="font-arabic-body leading-[1.8]" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.82rem, 1vw, 0.95rem)',
              opacity: 0.85
            }} data-id="element-438">
                لا يمكن ربط المخالفات المرورية بالسائق الفعلي بشكل تلقائي
              </p>
            </div>
            {/* Visual: disconnected link */}
            <div className="flex items-center justify-center gap-3 mt-[clamp(0.75rem,1.5vh,1rem)] opacity-40" data-id="element-439">
              <CarIcon size={16} color="#6E7A8F" strokeWidth={1.5} data-id="element-440" />
              <div className="flex items-center gap-1" data-id="element-441">
                <div className="w-4 h-px bg-[#D4553A]" data-id="element-442" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4553A]" data-id="element-443" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4553A]" data-id="element-444" />
                <div className="w-4 h-px bg-[#D4553A]" data-id="element-445" />
              </div>
              <UserXIcon size={16} color="#6E7A8F" strokeWidth={1.5} data-id="element-446" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== BOTTOM STATEMENT ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 1.3
    }} className="relative z-20 pb-[clamp(2rem,4vh,3rem)] pt-[clamp(0.5rem,1.5vh,1rem)] flex justify-center" data-id="element-447">
        <p className="font-arabic-body font-bold text-center" style={{
        color: '#0B1328',
        fontSize: 'clamp(0.92rem, 1.1vw, 1.05rem)'
      }} data-id="element-448">
          المركبة وحدها لا تكفي —{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-449">
            السائق
          </span>{' '}
          هو العنصر الأهم
        </p>
      </motion.div>
    </section>;
}